import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../api/client';

export interface ResumePhaseVars {
  jobId: string;
  phase: 'wash' | 'run';
  /** For the toast — the job number the office is looking at. */
  jobNumber?: string;
}

/**
 * Whether a phase can be resumed: it has ENDED and the job is not closed —
 * what POST .../phase/:phase/resume accepts (API 0534). A phase that is
 * running, or never started, has nothing to resume.
 */
export function phaseIsResumable(
  job: { status?: string | null; wash_ended_at?: string | null; run_ended_at?: string | null },
  phase: 'wash' | 'run',
): boolean {
  if (job.status === 'completed' || job.status === 'cancelled') return false;
  return (phase === 'wash' ? job.wash_ended_at : job.run_ended_at) != null;
}

/**
 * Resume an ended wash or run from the office (API 0534) — the crew is back
 * on a job after a gap (TNT J-4340). Opens a new stint; the board then draws
 * it from today and leaves the gap days empty.
 *
 * A refusal is the API's 409 sentence, shown verbatim.
 */
export function useResumePhaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, phase }: ResumePhaseVars) => api.dispatch.resumePhase(jobId, phase),
    onSuccess: (_res, { phase, jobNumber }) => {
      toast.success(`${phase === 'wash' ? 'Wash' : 'Run'} resumed${jobNumber ? ` on ${jobNumber}` : ''}.`);
    },
    onError: (err, { phase, jobNumber }) => {
      toast.error(getApiErrorMessage(err, `Could not resume the ${phase}${jobNumber ? ` on ${jobNumber}` : ''}.`));
    },
    onSettled: (_res, _err, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['field-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['field-my-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['field-dashboard'] });
      // An on_hold job going active again moves reserved gear into use.
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
    },
  });
}
